import type { FieldProposal } from '../../types';

import { classifyChange } from './DealsPage.changeKind';

/**
 * Uniform change indicator for FieldRow — renders a small pill
 * (+ / Δ / −) next to the field label when the field has a change.
 * Returns null when there's no change (caller can render unconditionally).
 *
 * Classification helper lives in DealsPage.changeKind.ts (separate file
 * so the component file only exports React components — react-refresh).
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §6.1.
 */

interface ChangeIndicatorProps {
  field: FieldProposal;
  indicatorClassName?: string;
  tagClassName?: string;
}

export function ChangeIndicator({
  field,
  indicatorClassName,
  tagClassName,
}: ChangeIndicatorProps) {
  const kind = classifyChange(field);
  if (!kind) return null;

  const symbol = kind === 'add' ? '+' : kind === 'remove' ? '−' : 'Δ';
  const title = kind === 'add' ? 'New value (no prior)' :
    kind === 'remove' ? 'Removed (prior was set)' :
    'Edited (prior and proposed differ)';

  return (
    <span
      className={indicatorClassName}
      data-change-kind={kind}
      aria-label={title}
    >
      <span className={tagClassName} title={title}>{symbol}</span>
    </span>
  );
}
